import React from "react";
import { Box } from "@mui/material";
import { PeopleAlt } from "@mui/icons-material";
import { HtmlTooltip } from "../StyledComponents/styled";

const sortedRoleNames = (roles) =>
    (Array.isArray(roles) ? [...roles] : [])
        .filter((roleName) => typeof roleName === "string" && roleName !== "")
        .sort((first, second) => first.localeCompare(second));

/** Same sorted role set on every company that has roles; otherwise null. */
const uniformRoleNamesAcrossCompanies = (companies) => {
    const roleSets = (Array.isArray(companies) ? companies : [])
        .map((company) => sortedRoleNames(company.roles))
        .filter((roles) => roles.length > 0);

    if (roleSets.length === 0) {
        return null;
    }

    const firstKey = JSON.stringify(roleSets[0]);
    const allMatch = roleSets.every((roles) => JSON.stringify(roles) === firstKey);
    return allMatch === true ? roleSets[0] : null;
};

const flatRolesTooltip = (roleNames) => {
    if (roleNames.length === 1) {
        return roleNames[0];
    }
    return (
        <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
            {roleNames.map((roleName) => (
                <li key={roleName}>{roleName}</li>
            ))}
        </Box>
    );
};

const groupedByCompanyTooltip = (grouped) => (
    <Box>
        {grouped.map((company) => (
            <Box key={company.name} sx={{ mb: grouped.length > 1 ? 1 : 0 }}>
                <Box sx={{ fontWeight: 700 }}>{company.name}</Box>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                    {company.roles.map((roleName) => (
                        <li key={company.name + "_" + roleName}>{roleName}</li>
                    ))}
                </Box>
            </Box>
        ))}
    </Box>
);

/**
 * Users-tab Role cell: one icon, hover lists roles grouped by company (A–Z).
 * companies: [{ name, roles: string[] }]
 * simplifyTooltipWhenUniform: flat role list when every company shares the same roles.
 */
const RolesByCompanyIcon = ({ companies = [], simplifyTooltipWhenUniform = false }) => {
    const grouped = (Array.isArray(companies) ? companies : [])
        .map((company) => ({
            name: company.name ?? "",
            roles: sortedRoleNames(company.roles),
        }))
        .filter((company) => company.name !== "" && company.roles.length > 0)
        .sort((first, second) => first.name.localeCompare(second.name));

    const uniformRoles =
        simplifyTooltipWhenUniform === true ? uniformRoleNamesAcrossCompanies(companies) : null;

    let tooltipTitle = "No roles assigned";
    if (grouped.length > 0) {
        if (uniformRoles !== null) {
            tooltipTitle = flatRolesTooltip(uniformRoles);
        } else {
            tooltipTitle = groupedByCompanyTooltip(grouped);
        }
    }

    return (
        <HtmlTooltip title={tooltipTitle}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
                {/* Default icon size, same as the edit and delete icons in this table. */}
                <PeopleAlt sx={{ color: "#AF3241" }} />
            </Box>
        </HtmlTooltip>
    );
};

export default RolesByCompanyIcon;
